import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { LabFeatureFlags } from 'constants/lab'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { getNavigationMenuIcon } from 'content/components/Icon/utils'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import useToggle from 'hooks/useToggle'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineUserCircleIcon from 'LuxKit/Icons/line/LineUserCircleIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { partitionBy } from 'lib/array/arrayUtils'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getFlatNavItems, MALDIVES_BUNDLES_NAV_ID } from 'selectors/navigationSelectors'
import styled from 'styled-components'
import NavBarCurrencyLanguageModal, { NavBarCurrencyLanguageDialog } from '../NavBarButtons/NavBarCurrencyLanguageModal'
import NavBarListItem from '../NavBarListItem'
import NavBarHamburgerMenuContactsAccordion from './NavBarHamburgerMenuContactsAccordion'
import NavBarHamburgerMenuLoyaltyFooter from './NavBarHamburgerMenuLoyaltyFooter'
import NavBarHamburgerMenuLoyaltyItem from './NavBarHamburgerMenuLoyaltyItem'
import NavBarHamburgerMenuMaldivesBundlesBanner from './NavBarHamburgerMenuMaldivesBundlesBanner'
import NavBarHamburgerMenuToggleItem from './NavBarHamburgerMenuToggleItem'

const NavItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(8)};
`

/** Max toggle tiles in the two-column grid (5 rows x 2). */
const GRID_ICON_SLOT_COUNT = 10

function navItemHasGridIcon(nav: App.NavItem): boolean {
  const name = nav.sideNavIconName || nav.iconName
  return Boolean(name && getNavigationMenuIcon(name))
}

function NavBarHamburgerMenu() {
  const intl = useIntl()
  const [showMore, toggleMore] = useToggle()
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const rawNavItems = useAppSelector(state => getFlatNavItems(state))
  const navItems = useLocalizedNavItems(rawNavItems)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const languageSelectorFlag = useLabFeatureFlag(LabFeatureFlags.enableLanguageSelector)
  const regionCurrencyTitle = intl.formatMessage({
    id: 'jZ_em4vQCeUJW4EVMsbw',
    defaultMessage: 'Select your region & currency',
    description: 'Modal title for region and currency selection in the navigation bar',
  })
  const regionCurrencyLanguageTitle = intl.formatMessage({
    id: 'niQoVEqXi3LmIJ_1E7Cc',
    defaultMessage: 'Region, currency & language',
    description: 'Modal title for region, currency and language selection in the navigation bar',
  })

  const onMoreClick = useCallback<MouseEventHandler<HTMLButtonElement>>((e) => {
    toggleMore()
    if (!showMore) {
      e.currentTarget.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [showMore, toggleMore])

  const onCurrencyLanguageClick = useCallback(() => {
    showModal(resolveModal => <NavBarCurrencyLanguageDialog
      height="full"
      size="S"
      tabletHeight="max"
      title={languageSelectorFlag?.enabled ? regionCurrencyLanguageTitle : regionCurrencyTitle}
    >
      <NavBarCurrencyLanguageModal
        showLanguageTab={!!languageSelectorFlag?.enabled}
        onSelect={resolveModal}
      />
    </NavBarCurrencyLanguageDialog>)
  }, [showModal, languageSelectorFlag?.enabled, regionCurrencyLanguageTitle, regionCurrencyTitle])
  const onLogin = useCallback(() => showModal(<AccountAccessModal />), [showModal])

  const closeModal = useCallback(() => resolve(), [resolve])
  const onNavItemClick = useCallback((_navItem: App.NavItem) => {
    closeModal()
  }, [closeModal])

  const maldivesBundlesItem = useMemo(
    () => navItems.find(nav => nav.id === MALDIVES_BUNDLES_NAV_ID),
    [navItems],
  )

  const navItemsForMenu = useMemo(
    () => navItems.filter(nav => nav.id !== MALDIVES_BUNDLES_NAV_ID),
    [navItems],
  )

  const [gridNavItems, moreItems] = useMemo(() => {
    const [primaryNavs, otherItems] = partitionBy(
      navItemsForMenu,
      nav => nav.category === 'primary',
    )
    const ordered = [...primaryNavs, ...otherItems]
    const iconsInOrder = ordered.filter(navItemHasGridIcon)
    const capped = Math.min(GRID_ICON_SLOT_COUNT, iconsInOrder.length)
    const evenCount = capped - (capped % 2)
    const grid = iconsInOrder.slice(0, evenCount)
    const gridSet = new Set(grid)
    const more = ordered.filter(item => !gridSet.has(item))
    return [grid, more]
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- `regionCode` forces recomputation on region change; nav array ref can lag
  }, [navItemsForMenu, regionCode])

  const seeMoreLabel = intl.formatMessage(
    {
      id: 'TglrUhyUNwTENosNXV1v',
      defaultMessage: 'See {count} more',
      description: 'Label on the expandable control in the hamburger menu that reveals additional navigation destinations, with the count of hidden items.',
    },
    { count: moreItems.length },
  )

  return <ModalBase mode="menu">
    <ModalHeader
      title={
        <Clickable onClick={closeModal} to={`/${config.defaultHomePage}`}>
          <LogoIcon height="1em" width={null} />
        </Clickable>
      }
    />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={20}>
          {!!gridNavItems.length && <NavItemGrid>
            {gridNavItems.map(item => <NavBarHamburgerMenuToggleItem
              key={item.id ?? item.text}
              navItem={item}
              onClick={() => onNavItemClick(item)}
            />)}
          </NavItemGrid>}
          {!!moreItems.length && <div>
            <ClickableListItem
              type="button"
              size="medium"
              title={seeMoreLabel}
              titleColour="neutral-one"
              endIcon={showMore
                ? <LineAngleUpIcon size="S" />
                : <LineAngleDownIcon size="S" />}
              onClick={onMoreClick}
            />
            <Expander expanded={showMore}>
              <Group direction="vertical" gap={8}>
                {moreItems.map(item => <ClickableListItem
                  key={item.id ?? item.text}
                  size="medium"
                  title={item.text}
                  titleColour="neutral-one"
                  to={item.to}
                  href={item.href}
                  onClick={() => onNavItemClick(item)}
                  {...(item.to || item.href ? {} : { type: 'button' as const })}
                />)}
              </Group>
            </Expander>
          </div>}
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            {maldivesBundlesItem && <NavBarHamburgerMenuMaldivesBundlesBanner
              navItem={maldivesBundlesItem}
              onNavigate={() => onNavItemClick(maldivesBundlesItem)}
            />}
            {luxPlusEnabled && <NavBarHamburgerMenuLoyaltyItem
              variant="luxPlus"
              onClick={closeModal}
            />}
            {loyaltyEnabled && <NavBarHamburgerMenuLoyaltyItem
              variant="loyalty"
              onClick={closeModal}
              isNew
            />}
          </Group>
          <NavBarHamburgerMenuContactsAccordion onItemClick={closeModal} />
          <VerticalSpacer gap={8}>
            <NavBarListItem
              title={currentCurrency}
              onClick={onCurrencyLanguageClick}
              startIcon={<Flag countryCode={currentRegionCode} size="S" />}
              endIcon={<CSSBreakpoint max="mobile" as={LineAngleRightIcon} />}
            />
          </VerticalSpacer>
        </Group>
      </ModalContent>
    </ModalBody>
    {!isCurrentRegionUserLoyaltyRegion && hasLoyaltyAccount && <NavBarHamburgerMenuLoyaltyFooter />}
    {!loggedIn && <ModalFooter>
      <TextButton
        kind="secondary"
        onClick={onLogin}
        size="large"
        fit="full-width"
        startIcon={<LineUserCircleIcon />}
      >
        {intl.formatMessage({
          id: 'DVut0himStXU8yyJTSJu',
          defaultMessage: 'Sign up or Login',
          description: 'Action button label for sign up or login action in the navigation bar',
        })}
      </TextButton>
    </ModalFooter>}
  </ModalBase>
}

export default NavBarHamburgerMenu
